import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// Client-side error reporting -> the reportClientError callable -> Cloud
// Error Reporting (grouped under service `limelight-web` in the GCP
// console). The web app's Crashlytics equivalent.
//
// Design invariants:
// - NEVER throws and never awaits into caller flow: a failing reporter must
//   not add a failure mode to an app that is already failing.
// - Client-side dedup: one report per minute per error signature and a hard
//   per-session cap, so a render loop cannot flood the backend (the callable
//   is rate-limited server-side too - this just avoids burning that budget).
// - Known browser noise is dropped before it costs a network call.

const REPORT_INTERVAL_MS = 60_000;
const SESSION_CAP = 10;

const lastReportAt = new Map<string, number>();
let sessionCount = 0;
let reporting = false;

const NOISE_PATTERNS = [
  /ResizeObserver loop/i,
  // Opaque cross-origin errors carry no information at all.
  /^Script error\.?$/i,
  // Firebase Performance's web-vitals attribution stores the interacted
  // element's CSS selector as a trace attribute, and a Tailwind class list
  // overruns the attribute-value limit - the SDK then throws inside its own
  // callback. Nothing of ours, nothing a user sees, and it was the loudest
  // entry in the functions error log (Aug 2026).
  /^Performance: Attribute value .* is invalid/,
];

interface ReportContext {
  componentStack?: string;
  source: 'boundary' | 'window.onerror' | 'unhandledrejection' | 'manual';
}

const signatureOf = (message: string, stack: string): string =>
  `${message}::${stack.split('\n')[1] ?? ''}`;

export function reportError(error: unknown, context: ReportContext): void {
  // Recursion guard: an error thrown while reporting is dropped outright.
  if (reporting) return;

  try {
    reporting = true;

    const message =
      error instanceof Error ? error.message : String(error ?? 'Unknown error');
    const stack = error instanceof Error ? error.stack ?? '' : '';

    if (!message || NOISE_PATTERNS.some((p) => p.test(message))) return;
    if (sessionCount >= SESSION_CAP) return;

    const signature = signatureOf(message, stack);
    const now = Date.now();
    const last = lastReportAt.get(signature);
    if (last !== undefined && now - last < REPORT_INTERVAL_MS) return;
    lastReportAt.set(signature, now);
    sessionCount++;

    const report = httpsCallable(functions, 'reportClientError');
    void report({
      message,
      stack,
      componentStack: context.componentStack ?? '',
      url: window.location.href,
      userAgent: navigator.userAgent,
      release: process.env.REACT_APP_VERSION ?? 'unknown',
      source: context.source,
    }).catch(() => {
      // Dropped report. Deliberately silent - see invariants above.
    });
  } catch {
    // Never let the reporter surface its own failure.
  } finally {
    reporting = false;
  }
}

/**
 * Global handlers for errors nothing else catches. Call once, early, from
 * index.tsx. The ErrorBoundary covers render errors; these cover event
 * handlers, async code and unhandled promise rejections.
 */
export function installGlobalErrorHandlers(): void {
  window.addEventListener('error', (event) => {
    reportError(event.error ?? event.message, { source: 'window.onerror' });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { source: 'unhandledrejection' });
  });
}
